// Copied verbatim from AmbassadorsLab/c3/frame-io-chooser (src/bridge/protocol.ts).
// Keep the two in sync; it is dependency-free precisely so it can be copied.
/**
 * Contract between this picker and the host app, kept small and dependency-free so it
 * can be copied into the host repo rather than published.
 *
 * The host must attach its own origin to the iframe URL:
 *   `${PICKER_ORIGIN}/?host=${encodeURIComponent(window.location.origin)}`
 * The picker will not post file data until it can identify the embedder, and
 * document.referrer is not dependable enough to rely on.
 *
 * FRAMEIO_READY may arrive more than once — React StrictMode replays effects in
 * development, and a reconnecting picker will re-announce. Handle it idempotently.
 */

/**
 * Rules for whatever consumes a selection. These travel with the contract because the
 * consumer lives in another repository and cannot infer them from the types.
 *
 * `source_url` and `thumbnail_url` are pre-signed CloudFront links that need no
 * credentials: anyone holding one can download the customer's original media until it
 * expires. Treat them as secrets — do not log them, do not pass them anywhere console
 * breadcrumbs are collected (Sentry captures console arguments by default), and do not
 * persist them beyond the ingest that consumes them.
 *
 * `name` is set by whoever uploaded the file. It may contain path separators,
 * bidirectional overrides, or an extension contradicting `media_type`. Display only.
 *
 * A backend fetching `source_url` should still validate its host, since a selection
 * message is only as trustworthy as the origin that sent it.
 */
export interface FrameioPickedFile {
  id: string;
  name: string;
  file_size: number;
  media_type: string | null;
  /** Pre-signed, needs no Authorization header. The host POSTs this as source_url. */
  source_url: string;
  /**
   * Unix seconds after which source_url stops working.
   *
   * Frame.io expires these at **midnight UTC**, not after a fixed interval, so the
   * lifetime depends on when the link was issued: ~17 hours in the morning, minutes
   * just before midnight. Re-requesting does not help — a URL issued at 23:55 still
   * expires at 00:00. Whatever fetches this must treat it as a deadline.
   */
  expires_at: number | null;
  thumbnail_url?: string;
  version_stack_id?: string;
}

export type FrameioOutboundMessage =
  | { type: 'FRAMEIO_READY' }
  | { type: 'FRAMEIO_CANCELLED' }
  | { type: 'FRAMEIO_FILES_SELECTED'; files: FrameioPickedFile[] }
  | { type: 'FRAMEIO_ERROR'; message: string };

export type FrameioInboundMessage =
  | { type: 'FRAMEIO_OPEN' }
  | { type: 'FRAMEIO_CLOSE' };

const hasType = (data: unknown, ...types: string[]): boolean =>
  types.includes((data as { type?: unknown })?.type as string);

export const isInboundMessage = (data: unknown): data is FrameioInboundMessage =>
  hasType(data, 'FRAMEIO_OPEN', 'FRAMEIO_CLOSE');

/** For the host: validate before acting on anything the picker sends. */
export const isOutboundMessage = (data: unknown): data is FrameioOutboundMessage =>
  hasType(
    data,
    'FRAMEIO_READY',
    'FRAMEIO_CANCELLED',
    'FRAMEIO_FILES_SELECTED',
    'FRAMEIO_ERROR'
  );
